import React, { useEffect, useRef, useState } from 'react';
import { configuracionDeAvisos, desactivarSuscripcion, guardarSuscripcion } from '../api';

/** La clave VAPID viaja en base64 de URL; el navegador la pide en bytes. */
const convertirClave = (valor) => {
  const relleno = '='.repeat((4 - (valor.length % 4)) % 4);
  const base64 = (valor + relleno).replace(/-/g, '+').replace(/_/g, '/');
  const bytes = atob(base64);
  return Uint8Array.from([...bytes].map((c) => c.charCodeAt(0)));
};

/**
 * En qué situación están los avisos en ESTE navegador.
 *
 * Se saca aparte porque decide si la persona se entera de que no le está llegando nada, y
 * equivocarse aquí se nota justamente por el silencio.
 *
 *   no-soportado : el navegador no puede, o el sitio no va por HTTPS. No hay nada que ofrecer.
 *   bloqueado    : la persona -o Windows- le quitó el permiso. Un clic NO lo arregla: hay que
 *                  devolverlo desde el navegador, así que solo se explica cómo.
 *   inactivo     : se puede activar. Es el único caso con un botón que sirva.
 *   activo       : permiso dado Y canal creado. Lo único que de verdad significa que llegan.
 *
 * El caso que hay que atrapar es "había canal pero el permiso estaba revocado": se daría por
 * activo, no llegaría nada, y como el botón se esconde al estar activo la persona no tendría ni
 * cómo enterarse ni cómo arreglarlo. Por eso el permiso se mira ANTES que el canal.
 */
export function estadoDeAvisos({ soportado, permiso, haySuscripcion }) {
  if (!soportado) return 'no-soportado';
  if (permiso === 'denied') return 'bloqueado';
  if (permiso !== 'granted') return 'inactivo';
  return haySuscripcion ? 'activo' : 'inactivo';
}

/**
 * Prende y apaga los avisos en la pantalla del teléfono o de la computadora.
 *
 * El permiso del navegador SOLO se puede pedir desde un clic de verdad — por eso es un botón y
 * no algo automático al entrar. Y se pide una vez: si la persona dice que no, el navegador no
 * lo vuelve a preguntar, así que insistir sola no sirve de nada.
 */
export default function ActivarAvisos({ alAvisar }) {
  const [estado, setEstado] = useState('cargando');
  const [trabajando, setTrabajando] = useState(false);
  const enCurso = useRef(false);

  useEffect(() => {
    const soportado = window.isSecureContext && 'serviceWorker' in navigator
      && 'PushManager' in window && 'Notification' in window;

    if (!soportado || Notification.permission !== 'granted') {
      setEstado(estadoDeAvisos({
        soportado, permiso: soportado ? Notification.permission : null, haySuscripcion: false,
      }));
      return;
    }
    navigator.serviceWorker.ready
      .then((registro) => registro.pushManager.getSubscription())
      .then((suscripcion) => {
        setEstado(estadoDeAvisos({
          soportado, permiso: Notification.permission, haySuscripcion: !!suscripcion,
        }));
        if (!suscripcion) return null;
        // Reclamar el canal para QUIEN ESTÁ DENTRO AHORA.
        //
        // Un navegador tiene UN solo canal de avisos, y en el servidor queda a nombre del
        // último que lo registró. En una computadora compartida —dos personas de la misma
        // empresa, cada una con su cuenta— el canal se lo llevaría la última, y la anterior
        // dejaría de recibir sin enterarse: el navegador SÍ tiene canal, así que el botón ni
        // le aparece. Re-registrarlo en cada carga corrige la titularidad solo.
        return guardarSuscripcion(suscripcion.toJSON()).catch(() => {});
      })
      .catch(() => setEstado('inactivo'));
  }, []);

  const activar = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setTrabajando(true);
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== 'granted') throw new Error('PERMISO');

      const config = (await configuracionDeAvisos()).data;
      if (!config?.disponible || !config?.vapidPublicKey) throw new Error('CONFIG');

      const registro = await navigator.serviceWorker.ready;
      let suscripcion = await registro.pushManager.getSubscription();
      if (!suscripcion) {
        suscripcion = await registro.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertirClave(config.vapidPublicKey),
        });
      }
      await guardarSuscripcion(suscripcion.toJSON());
      setEstado('activo');
      alAvisar?.('Listo. Le avisaremos cuando alguno de sus trámites avance.', 'ok');
    } catch (error) {
      alAvisar?.(
        error.message === 'PERMISO'
          ? 'El navegador no dio permiso para mostrar avisos. Puede activarlo desde el candado 🔒 de la barra de direcciones.'
          : error.message === 'CONFIG'
            ? 'Los avisos todavía no están configurados. Avísele a WF Aduanas.'
            : 'No se pudieron activar los avisos en este dispositivo.',
        'error');
    } finally {
      enCurso.current = false;
      setTrabajando(false);
    }
  };

  const apagar = async () => {
    if (enCurso.current) return;
    enCurso.current = true;
    setTrabajando(true);
    try {
      const registro = await navigator.serviceWorker.ready;
      const suscripcion = await registro.pushManager.getSubscription();
      if (suscripcion) {
        // Primero se da de baja en el servidor y después en el navegador: al revés, si lo
        // segundo falla, el servidor seguiría mandando avisos a un canal que ya no existe.
        await desactivarSuscripcion(suscripcion.endpoint).catch(() => {});
        await suscripcion.unsubscribe().catch(() => {});
      }
      setEstado('inactivo');
      alAvisar?.('Ya no le vamos a mandar avisos a este dispositivo.', 'ok');
    } catch {
      alAvisar?.('No se pudieron apagar los avisos.', 'error');
    } finally {
      enCurso.current = false;
      setTrabajando(false);
    }
  };

  if (estado === 'cargando' || estado === 'no-soportado') return null;

  // Bloqueado no se arregla con un clic: el permiso lo tiene que devolver la persona desde el
  // navegador. Se muestra igual, porque el silencio era justo el problema.
  if (estado === 'bloqueado') {
    const explicacion = 'Los avisos están bloqueados en este navegador. Dele al candado 🔒 de la '
      + 'barra de direcciones y permita las notificaciones para este sitio.';
    return (
      <button type="button" className="btn-barra" title={explicacion}
        onClick={() => alAvisar?.(explicacion, 'error')}>
        🔕<span className="solo-ancho-boton"> Avisos bloqueados</span>
      </button>
    );
  }

  const activos = estado === 'activo';
  return (
    <button
      type="button"
      className="btn-barra"
      onClick={activos ? apagar : activar}
      disabled={trabajando}
      title={activos
        ? 'Le avisamos en este dispositivo cuando un trámite avance. Toque para apagarlos.'
        : 'Reciba un aviso en este dispositivo cuando alguno de sus trámites avance.'}
    >
      {trabajando ? '…' : (
        <>
          {activos ? '🔔' : '🔕'}
          <span className="solo-ancho-boton">{activos ? ' Avisos activos' : ' Activar avisos'}</span>
        </>
      )}
    </button>
  );
}
